import type { ReactNode } from "react"

import cx from "classnames"

import { buttonColors, buttonSizes, buttonTypes, type ButtonColor, type ButtonSize, type ButtonType } from "./config"
import styles from "./index.module.css"

/**
 * Props for the Button component.
 */
export type ButtonProps = Pick<
  React.ComponentPropsWithoutRef<"button">,
  "children" | "onClick" | "form" | "value" | "name" | "className"
> & {
  /**
   * The variant of the button.
   */
  type?: ButtonType

  /**
   * The color scheme of the button.
   */
  color?: ButtonColor

  /**
   * The size of the button.
   */
  size?: ButtonSize

  /**
   * Optional left icon element.
   */
  iconLeft?: ReactNode

  /**
   * Optional right icon element.
   */
  iconRight?: ReactNode

  /**
   * Whether the button should expand to fill its container width.
   */
  isFull?: boolean

  /**
   * Whether the button has a gradient border.
   */
  isGradientBorder?: boolean

  /**
   * Whether the button hides text on mobile devices.
   */
  hideTextOnMobile?: boolean

  /**
   * Whether the button is a submit button.
   */
  isSubmit?: boolean

  /**
   * The HTML type of the button (button, submit, reset).
   */
  htmlType?: "button" | "submit" | "reset"

  /**
   * Whether the button is disabled.
   */
  isDisabled?: boolean

  /**
   * Whether the button is in a loading state.
   */
  isLoading?: boolean
  /**
   * The HTML id of the button.
   */
  htmlId?: string
}

/**
 * Maps button variant to corresponding CSS class.
 */
const typeClass = {
  [buttonTypes.FILLED]: styles.filled,
  [buttonTypes.OUTLINE]: styles.outline,
  [buttonTypes.TEXT]: styles.text,
}

/**
 * Maps button color to corresponding CSS class.
 */
const colorClass = {
  [buttonColors.PRIMARY]: styles.colorPrimary,
  [buttonColors.ACCENT]: styles.colorAccent,
  [buttonColors.ERROR]: styles.colorError,
  [buttonColors.SUCCESS]: styles.colorSuccess,
  [buttonColors.WARNING]: styles.colorWarning,
  [buttonColors.INFO]: styles.colorInfo,
  [buttonColors.SURFACE_INVERSE]: styles.colorSurfaceInverse,
  [buttonColors.ON_SURFACE_INVERSE]: styles.colorOnSurfaceInverse,
}

/**
 * A customizable button component.
 * @param {ButtonProps} props - The props for the Button component.
 * @returns {React.JSX.Element} - A React JSX element representing the button.
 */
export const Button = ({
  color = buttonColors.PRIMARY,
  size = buttonSizes.MEDIUM,
  type = buttonTypes.FILLED,
  iconLeft,
  iconRight,
  form,
  isDisabled = false,
  isFull = false,
  isLoading = false,
  isSubmit,
  isGradientBorder = false,
  hideTextOnMobile = false,
  htmlId,
  htmlType = "button",
  children,
  className,
  ...htmlProps
}: ButtonProps): React.JSX.Element => {
  const classSize = styles[size || buttonSizes.MEDIUM]
  const buttonClasses = cx(
    styles.button,
    colorClass[color ?? buttonColors.PRIMARY],
    typeClass[type ?? buttonTypes.FILLED],
    classSize,
    {
      [styles.onlyIconButton]: !children,
      [styles.fullWidth]: isFull,
      [styles.disabled]: isDisabled,
      [styles.loading]: isLoading,
      [styles.gradientBorder]: isGradientBorder,
      [styles.hideTextMobile]: hideTextOnMobile,
    },
    className,
  )

  const iconClasses = cx(styles.icon, {
    [styles.onlyIcon]: !children,
  })

  const buttonContent = (
    <>
      {iconLeft && <span className={iconClasses}>{iconLeft}</span>}
      {children && <div className={styles.textContent}>{children}</div>}
      {iconRight && <span className={iconClasses}>{iconRight}</span>}
    </>
  )

  return (
    <button
      disabled={isDisabled || isLoading}
      type={isSubmit ? "submit" : htmlType}
      className={buttonClasses}
      form={form}
      id={htmlId}
      {...htmlProps}
    >
      {buttonContent}
    </button>
  )
}
